import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AI_CONNECTORS } from "@/constants/testIds";
import { toast } from "sonner";
import { Plug, Plus, Save, Trash2, PlayCircle, CheckCircle2, XCircle, Info, X, Eye, EyeOff, Zap, RefreshCw } from "lucide-react";

export const KIND_META = {
  anthropic_direct: {
    label: "Anthropic (your key)",
    icon: "🅐",
    color: "bg-orange-50 text-orange-700 border-orange-200",
    default_model: "claude-sonnet-4-5-20250929",
    key_hint: "Get your key from console.anthropic.com → API Keys. Starts with 'sk-ant-'.",
    requires_key: true,
    docs: "https://console.anthropic.com/settings/keys",
  },
  openai_direct: {
    label: "OpenAI (your key)",
    icon: "🅞",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    default_model: "gpt-4o-mini",
    key_hint: "Get your key from platform.openai.com → API Keys. Starts with 'sk-'.",
    requires_key: true,
    docs: "https://platform.openai.com/api-keys",
  },
  emergent_claude: {
    label: "Claude via Emergent Universal Key",
    icon: "🅔",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    default_model: "claude-sonnet-4-5-20250929",
    key_hint: "Uses your Emergent Universal Key (already configured on the server).",
    requires_key: false,
  },
  emergent_openai: {
    label: "OpenAI via Emergent Universal Key",
    icon: "🅔",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    default_model: "gpt-5.2",
    key_hint: "Uses your Emergent Universal Key (already configured on the server).",
    requires_key: false,
  },
  emergent_gemini: {
    label: "Gemini via Emergent Universal Key",
    icon: "🅔",
    color: "bg-indigo-50 text-indigo-700 border-indigo-200",
    default_model: "gemini-2.5-flash",
    key_hint: "Uses your Emergent Universal Key (already configured on the server).",
    requires_key: false,
  },
  ollama: {
    label: "Ollama (self-hosted, free)",
    icon: "🦙",
    color: "bg-slate-100 text-slate-800 border-slate-300",
    default_model: "llama3.1:8b",
    key_hint: "Install Ollama on your server: `curl -fsSL https://ollama.com/install.sh | sh` — then pull a model.",
    requires_key: false,
  },
  mock: {
    label: "Mock (dev / no LLM cost)",
    icon: "🧪",
    color: "bg-slate-100 text-slate-600 border-slate-200",
    default_model: "mock",
    key_hint: "Returns canned responses. Useful when clicking around the UI without spending on LLMs.",
    requires_key: false,
  },
};

const KIND_ORDER = [
  "anthropic_direct", "openai_direct",
  "emergent_claude", "emergent_openai", "emergent_gemini",
  "ollama", "mock",
];

export default function AIConnectors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai-connectors");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({
    kind: "anthropic_direct",
    name: "My Anthropic",
    model: KIND_META.anthropic_direct.default_model,
    api_key: "",
    extra_config: {},
  });
  const openEdit = (row) => setEditing({
    id: row.id,
    kind: row.kind,
    name: row.name,
    model: row.model,
    api_key: "",  // must retype if changing
    extra_config: row.extra_config || {},
  });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Name is required");
    const meta = KIND_META[editing.kind];
    if (meta.requires_key && !editing.id && !editing.api_key.trim()) {
      return toast.error(`API key required for ${meta.label}`);
    }
    try {
      if (editing.id) {
        const payload = {
          name: editing.name.trim(),
          model: editing.model,
          extra_config: editing.extra_config,
        };
        if (editing.api_key && editing.api_key.trim()) payload.api_key = editing.api_key.trim();
        await api.put(`/ai-connectors/${editing.id}`, payload);
      } else {
        await api.post("/ai-connectors", {
          kind: editing.kind,
          name: editing.name.trim(),
          model: editing.model || meta.default_model,
          api_key: editing.api_key.trim(),
          extra_config: editing.extra_config,
        });
      }
      toast.success(editing.id ? "Connector updated" : "Connector created");
      setEditing(null); setShowKey(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (row) => {
    if (row.is_active) return toast.error("Cannot delete the active connector. Activate a different one first.");
    // Two-click confirm — native confirm() is blocked by the preview iframe.
    if (confirmingDelete !== row.id) {
      setConfirmingDelete(row.id);
      setTimeout(() => setConfirmingDelete((cur) => (cur === row.id ? "" : cur)), 5000);
      return;
    }
    setConfirmingDelete("");
    try {
      await api.delete(`/ai-connectors/${row.id}`);
      toast.success("Connector deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const activate = async (row) => {
    try {
      await api.post(`/ai-connectors/${row.id}/activate`);
      toast.success(`"${row.name}" is now the active AI`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activate failed");
    }
  };

  const testConn = async (row) => {
    setTestingId(row.id);
    try {
      const { data } = await api.post(`/ai-connectors/${row.id}/test`);
      if (data.ok) toast.success(`OK · ${data.latency_ms} ms · Reply: ${(data.reply || "").slice(0, 60)}`);
      else toast.error(`Failed · ${data.error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestingId(null);
    }
  };

  const activeConnector = rows.find((r) => r.is_active);
  const sortedRows = [...rows].sort((a, b) => (KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind)));

  return (
    <div data-testid={AI_CONNECTORS.root} className="p-8 lg:p-12 max-w-[1200px]">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500 mb-1">AI Configuration</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter flex items-center gap-2">
            <Plug className="h-7 w-7 text-orange-600" /> AI Connectors
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Plug in any LLM provider — your own Anthropic/OpenAI key, or Emergent Universal Key, or a
            self-hosted Ollama box. <b>Only one connector is active at a time</b>; every AI call in the app
            (drafts, scoring, digests, Ask-Pippy) uses the active one.
          </p>
        </div>
        <button
          data-testid={AI_CONNECTORS.addBtn}
          onClick={openNew}
          className="inline-flex items-center gap-2 bg-orange-600 text-white text-xs font-semibold px-3 py-2 rounded-md hover:bg-orange-500 transition-colors duration-150"
        >
          <Plus className="h-3.5 w-3.5" /> New connector
        </button>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3 mb-6 flex gap-2 items-start">
        <Info className="h-3.5 w-3.5 text-indigo-500 mt-0.5 shrink-0" />
        <div className="text-xs text-indigo-900 leading-relaxed">
          <b>Cost tips:</b> Anthropic (direct) → billed per token by Anthropic — check
          <a className="underline mx-1" href="https://console.anthropic.com" target="_blank" rel="noreferrer">console.anthropic.com</a>
          for exact usage. Ollama runs on your own server → 0 cost. Emergent Universal Key → shared credit
          pool visible in your Emergent profile → Universal Key.
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Provider</th>
              <th className="text-left px-4 py-3 font-semibold">Name</th>
              <th className="text-left px-4 py-3 font-semibold">Model</th>
              <th className="text-left px-4 py-3 font-semibold">API key</th>
              <th className="text-left px-4 py-3 font-semibold">Last test</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="w-64" />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-center text-slate-500 text-sm">Loading…</td></tr>}
            {!loading && sortedRows.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500 text-sm">
                No connectors yet — click "New connector" to add one.
              </td></tr>
            )}
            {sortedRows.map((row) => {
              const meta = KIND_META[row.kind] || KIND_META.mock;
              return (
                <tr key={row.id} data-testid={`${AI_CONNECTORS.rowPrefix}${row.id}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150">
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded border ${meta.color}`}>
                      {meta.icon} {meta.label.split(" ").slice(0, 2).join(" ")}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold">{row.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.model || meta.default_model}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.api_key_masked || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    {row.last_test_status === "ok" && (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <CheckCircle2 className="h-3.5 w-3.5" /> {row.last_test_ms}ms
                      </span>
                    )}
                    {row.last_test_status === "failed" && (
                      <span className="inline-flex items-center gap-1 text-red-700 font-semibold" title={row.last_test_error || ""}>
                        <XCircle className="h-3.5 w-3.5" /> failed
                      </span>
                    )}
                    {!row.last_test_status && <span className="text-slate-400">never</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded">
                        <Zap className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest text-slate-400">standby</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap space-x-2">
                    <button
                      data-testid={`${AI_CONNECTORS.testBtn}-${row.id}`}
                      onClick={() => testConn(row)}
                      disabled={testingId === row.id}
                      className="text-xs font-semibold text-slate-700 hover:text-orange-600 inline-flex items-center gap-1"
                    >
                      {testingId === row.id ? <RefreshCw className="h-3 w-3 animate-spin" /> : <PlayCircle className="h-3 w-3" />}
                      Test
                    </button>
                    {!row.is_active && (
                      <button
                        data-testid={`${AI_CONNECTORS.activateBtn}-${row.id}`}
                        onClick={() => activate(row)}
                        className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
                      >
                        Activate
                      </button>
                    )}
                    <button onClick={() => openEdit(row)} className="text-xs font-semibold text-slate-700 hover:text-orange-600">Edit</button>
                    <button
                      onClick={() => remove(row)}
                      disabled={row.is_active}
                      className={`text-xs font-semibold inline-flex items-center gap-1 disabled:text-slate-300 disabled:hover:text-slate-300 transition-colors duration-150 ${confirmingDelete === row.id ? "text-white bg-red-600 px-2 py-0.5 rounded" : "text-red-600 hover:text-red-800"}`}
                      title={row.is_active ? "Activate a different connector first" : (confirmingDelete === row.id ? "Click again to confirm" : `Delete "${row.name}"`)}
                    >
                      <Trash2 className="h-3 w-3" /> {confirmingDelete === row.id ? "Click to confirm" : "Delete"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setEditing(null); setShowKey(false); }}>
          <div className="bg-white rounded-lg border border-slate-200 shadow-lg w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg tracking-tight">
                {editing.id ? "Edit connector" : "New connector"}
              </h3>
              <button onClick={() => { setEditing(null); setShowKey(false); }}><X className="h-4 w-4 text-slate-500" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Provider</label>
                <select
                  data-testid={AI_CONNECTORS.kindSelect}
                  value={editing.kind}
                  disabled={!!editing.id}
                  onChange={(e) => {
                    const kind = e.target.value;
                    setEditing({
                      ...editing,
                      kind,
                      model: KIND_META[kind].default_model,
                    });
                  }}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none transition-colors duration-150 disabled:bg-slate-50"
                >
                  {KIND_ORDER.map((k) => (
                    <option key={k} value={k}>{KIND_META[k].icon} {KIND_META[k].label}</option>
                  ))}
                </select>
                <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                  {KIND_META[editing.kind].key_hint}
                  {KIND_META[editing.kind].docs && (
                    <> · <a href={KIND_META[editing.kind].docs} target="_blank" rel="noreferrer" className="underline text-orange-600">open dashboard</a></>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Display name</label>
                <input
                  data-testid={AI_CONNECTORS.nameInput}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="e.g., Prod Anthropic (company card)"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">
                  Model <span className="text-slate-500 font-normal">(default: {KIND_META[editing.kind].default_model})</span>
                </label>
                <input
                  data-testid={AI_CONNECTORS.modelInput}
                  value={editing.model || ""}
                  onChange={(e) => setEditing({ ...editing, model: e.target.value })}
                  placeholder={KIND_META[editing.kind].default_model}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                />
              </div>

              {KIND_META[editing.kind].requires_key && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 flex items-center justify-between">
                    <span>API key {editing.id && <span className="text-slate-500 font-normal ml-1">(leave blank to keep current)</span>}</span>
                    <button
                      type="button"
                      onClick={() => setShowKey((s) => !s)}
                      className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                    >
                      {showKey ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      {showKey ? "hide" : "show"}
                    </button>
                  </label>
                  <input
                    data-testid={AI_CONNECTORS.keyInput}
                    type={showKey ? "text" : "password"}
                    value={editing.api_key || ""}
                    onChange={(e) => setEditing({ ...editing, api_key: e.target.value })}
                    placeholder={editing.kind === "anthropic_direct" ? "sk-ant-…" : "sk-…"}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                  />
                  <div className="text-[11px] text-slate-500 mt-1">
                    Stored server-side only. Never sent back to your browser. You can revoke the key any time on the provider dashboard.
                  </div>
                </div>
              )}

              {editing.kind === "ollama" && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">
                    Ollama host <span className="text-slate-500 font-normal">(default: http://localhost:11434)</span>
                  </label>
                  <input
                    value={editing.extra_config?.host || ""}
                    onChange={(e) => setEditing({ ...editing, extra_config: { ...editing.extra_config, host: e.target.value } })}
                    placeholder="http://localhost:11434"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => { setEditing(null); setShowKey(false); }} className="text-xs px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button
                data-testid={AI_CONNECTORS.saveBtn}
                onClick={save}
                className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md bg-orange-600 text-white font-semibold hover:bg-orange-500 transition-colors duration-150"
              >
                <Save className="h-3.5 w-3.5" /> {editing.id ? "Save changes" : "Create connector"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
